import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CorrectionBadge } from '@/components/shared/CorrectionBadge';
import { CopyValueButton } from '@/components/shared/CopyValueButton';
import { isRowNavHandled } from '@/components/shared/rowNav';
import { formatPLN } from '@/utils/currency';
import { monthEnd, formatMonthYear } from '@/lib/hr';
import { getLanguage, t } from '@/i18n';
import { useInvoicesApi } from '@/services/api';
import type { InvoiceListItem } from '@ip/shared-types';

interface SalesReportViewProps {
  companyNip: string;
  onInvoiceSelect: (invoiceId: string) => void;
}

const COLUMN_COUNT = 8;
// Server-side hard cap (GetInvoicesQueryDto @Max(100)) — a month with more
// invoices is fetched by looping pages.
const PAGE_SIZE = 100;
// Year picker floor — legacy EPP imports (source='import') carry sales well
// before the app's own history; anything older is still reachable via a
// hand-set ?month= (the selected year is pushed into the options).
const EARLIEST_YEAR = 2010;
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Previous calendar month as YYYY-MM — the report's default. */
function previousMonth(): string {
  return dayjs().subtract(1, 'month').format('YYYY-MM');
}

function formatDate(date: string | null): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString(getLanguage() === 'pl' ? 'pl-PL' : 'en-GB');
}

/** Sales-month convention: a missing saleDate means "sale date = issue date". */
function displaySaleDate(inv: InvoiceListItem): string | null {
  return inv.saleDate ?? inv.issueDate;
}

/**
 * Final report order: issue date, then (same issue day, several FVs) sale
 * date, then id for stability. Client-side because the sale-date key uses the
 * COALESCE convention the server can't order by; nulls sort last (matching
 * the server's issue-date order the pages arrive in).
 */
function reportOrder(a: InvoiceListItem, b: InvoiceListItem): number {
  const key = (d: string | null) => d ?? '9999-99-99';
  return (
    key(a.issueDate).localeCompare(key(b.issueDate)) ||
    key(displaySaleDate(a)).localeCompare(key(displaySaleDate(b))) ||
    a.id.localeCompare(b.id)
  );
}

export function SalesReportView({ companyNip, onInvoiceSelect }: SalesReportViewProps) {
  // The month lives in the URL (like useSalesFilters) so it survives
  // navigating into an invoice detail and back; the default is omitted.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawMonth = searchParams.get('month');
  const month = rawMonth && MONTH_RE.test(rawMonth) ? rawMonth : previousMonth();

  const setMonth = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === previousMonth()) params.delete('month');
          else params.set('month', next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const loadSeq = useRef(0);

  const { getInvoices } = useInvoicesApi();

  // Whole sales month in one list: sale_or_issue_date filters on
  // COALESCE(date_of_sale, invoice_issue_date) server-side, sorted by issue
  // date ascending; pages are looped past the 100-per-page cap.
  useEffect(() => {
    if (!companyNip) return;
    const seq = ++loadSeq.current;
    setLoading(true);
    (async () => {
      try {
        const dateFrom = `${month}-01`;
        const dateTo = monthEnd(dateFrom);
        const all: InvoiceListItem[] = [];
        // Belt-and-braces dedupe: an insert between page fetches shifts
        // offsets, which could repeat a boundary row (also a React key clash).
        const seen = new Set<string>();
        let fetched = 0;
        let page = 1;
        for (;;) {
          const result = await getInvoices(companyNip, {
            direction: 'sale',
            dateType: 'sale_or_issue_date',
            dateFrom,
            dateTo,
            page,
            pageSize: PAGE_SIZE,
            sortBy: 'issueDate',
            sortOrder: 'asc',
          });
          if (seq !== loadSeq.current) return;
          fetched += result.items.length;
          for (const item of result.items) {
            if (!seen.has(item.id)) {
              seen.add(item.id);
              all.push(item);
            }
          }
          if (fetched >= result.total || result.items.length === 0) break;
          page += 1;
        }
        all.sort(reportOrder);
        setInvoices(all);
      } catch {
        if (seq === loadSeq.current) toast.error(t('errors.loadingFailed'));
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    })();
  }, [companyNip, month, getInvoices]);

  const [yearStr, monthStr] = month.split('-');
  const locale = getLanguage() === 'pl' ? 'pl-PL' : 'en-GB';
  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: String(i + 1).padStart(2, '0'),
        label: new Date(Date.UTC(2000, i, 1)).toLocaleDateString(locale, {
          month: 'long',
          timeZone: 'UTC',
        }),
      })),
    [locale],
  );
  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(() => {
    const years = Array.from(
      { length: Math.max(currentYear - EARLIEST_YEAR + 1, 1) },
      (_, i) => currentYear - i,
    );
    const selected = Number(yearStr);
    if (!years.includes(selected)) years.push(selected);
    return years.sort((a, b) => b - a);
  }, [currentYear, yearStr]);

  const columnLabels = [
    t('sales.report.columns.lp'),
    t('sales.report.columns.saleDate'),
    t('sales.report.columns.issueDate'),
    t('sales.report.columns.invoiceNumber'),
    t('sales.report.columns.ksefNumber'),
    t('sales.report.columns.customer'),
    t('sales.report.columns.netAmount'),
    t('sales.report.columns.currency'),
  ];

  // TSV with a header row — pastes into Excel one value per cell. Amounts use
  // a decimal comma (as displayed) but NO nbsp thousand separators, so Polish
  // Excel parses them as numbers.
  // Free-text cells (customer name, invoice number) are OCR/KSeF-derived and
  // may embed tabs/newlines that would shift Excel cells — collapse them.
  const tsvCell = (v: string) => v.replace(/[\t\r\n]+/g, ' ');
  const writeToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('sales.report.copied'));
    } catch {
      toast.error(t('sales.report.copyError'));
    }
  };
  const handleCopy = () => {
    const rows = invoices.map((inv, i) =>
      [
        String(i + 1),
        formatDate(displaySaleDate(inv)),
        formatDate(inv.issueDate),
        inv.invoiceNumber ?? '-',
        inv.ksefNumber ?? '-',
        inv.customerName ?? '-',
        inv.netAmount !== null ? inv.netAmount.toFixed(2).replace('.', ',') : '-',
        inv.currency ?? '-',
      ]
        .map(tsvCell)
        .join('\t'),
    );
    return writeToClipboard([columnLabels.join('\t'), ...rows].join('\n'));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-lg font-semibold">
          {t('sales.report.title', { month: formatMonthYear(`${month}-01`) })}
        </h3>
        <div className="ml-auto flex items-center gap-2">
          <Select value={monthStr} onValueChange={(v) => setMonth(`${yearStr}-${v}`)}>
            <SelectTrigger size="sm" className="w-36" aria-label={t('sales.report.monthLabel')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={yearStr} onValueChange={(v) => setMonth(`${v}-${monthStr}`)}>
            <SelectTrigger size="sm" className="w-24" aria-label={t('sales.report.yearLabel')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            disabled={loading || invoices.length === 0}
          >
            <Copy className="size-4" />
            {t('sales.report.copy')}
          </Button>
        </div>
      </div>

      <div className="bg-card rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-muted-foreground w-12">{columnLabels[0]}</TableHead>
                <TableHead className="text-muted-foreground">{columnLabels[1]}</TableHead>
                <TableHead className="text-muted-foreground">{columnLabels[2]}</TableHead>
                <TableHead className="text-muted-foreground">{columnLabels[3]}</TableHead>
                <TableHead className="text-muted-foreground">{columnLabels[4]}</TableHead>
                <TableHead className="text-muted-foreground">{columnLabels[5]}</TableHead>
                <TableHead className="text-muted-foreground text-right">{columnLabels[6]}</TableHead>
                <TableHead className="text-muted-foreground">{columnLabels[7]}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 8 }, (_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="h-40 text-center">
                    <p className="font-medium">{t('sales.noInvoices')}</p>
                    <p className="text-muted-foreground text-sm">
                      {t('sales.report.emptyDescription')}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((inv, i) => (
                  <TableRow
                    key={inv.id}
                    className="group cursor-pointer"
                    onClick={(e) => {
                      if (isRowNavHandled(e)) return;
                      onInvoiceSelect(inv.id);
                    }}
                  >
                    <TableCell className="text-muted-foreground py-2 tabular-nums">
                      {i + 1}
                    </TableCell>
                    <TableCell className="py-2 tabular-nums">
                      <span className="inline-flex items-center gap-1">
                        {formatDate(displaySaleDate(inv))}
                        <CopyValueButton value={formatDate(displaySaleDate(inv))} />
                      </span>
                    </TableCell>
                    <TableCell className="py-2 tabular-nums">
                      <span className="inline-flex items-center gap-1">
                        {formatDate(inv.issueDate)}
                        <CopyValueButton value={formatDate(inv.issueDate)} />
                      </span>
                    </TableCell>
                    <TableCell className="py-2">
                      <span className="flex items-center gap-1.5">
                        <span className="font-mono text-sm">{inv.invoiceNumber || '-'}</span>
                        <CorrectionBadge rodzajFaktury={inv.rodzajFaktury} />
                        {inv.invoiceNumber && <CopyValueButton value={inv.invoiceNumber} />}
                      </span>
                    </TableCell>
                    <TableCell className="py-2">
                      {inv.ksefNumber ? (
                        <span className="inline-flex items-center gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-muted-foreground inline-block max-w-40 truncate align-bottom font-mono text-xs">
                                {inv.ksefNumber}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{inv.ksefNumber}</TooltipContent>
                          </Tooltip>
                          <CopyValueButton value={inv.ksefNumber} />
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      <p
                        className="max-w-64 truncate text-sm"
                        title={inv.customerName || inv.customerNip || '-'}
                      >
                        {inv.customerName || '-'}
                      </p>
                    </TableCell>
                    <TableCell className="py-2 text-right font-mono text-sm tabular-nums">
                      {inv.netAmount !== null ? formatPLN(inv.netAmount) : '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground py-2 text-sm">
                      {inv.currency || '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {!loading && invoices.length > 0 && (
          <div className="text-muted-foreground border-t px-3 py-2 text-sm">
            {t('sales.report.count', { count: invoices.length })}
          </div>
        )}
      </div>
    </div>
  );
}
